import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

interface SheetProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SheetContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  mountPointRef: React.RefObject<HTMLDivElement>;
}>({
  isOpen: false,
  setIsOpen: () => {},
  mountPointRef: { current: null },
});

const Sheet = ({ children, open, onOpenChange }: SheetProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const mountPointRef = React.useRef<HTMLDivElement>(null);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <div ref={mountPointRef} style={{ display: "contents" }}>
      <SheetContext.Provider
        value={{
          isOpen: open ?? isOpen,
          setIsOpen: handleOpenChange,
          mountPointRef,
        }}
      >
        {children}
      </SheetContext.Provider>
    </div>
  );
};

interface SheetTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children: React.ReactNode;
}

const SheetTrigger = React.forwardRef<HTMLButtonElement, SheetTriggerProps>(
  ({ className, children, asChild, ...props }, ref) => {
    const { setIsOpen } = React.useContext(SheetContext);

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement, {
        onClick: () => setIsOpen(true),
        ref,
      });
    }

    return (
      <button
        ref={ref}
        type="button"
        className={cn(className)}
        onClick={() => setIsOpen(true)}
        {...props}
      >
        {children}
      </button>
    );
  }
);
SheetTrigger.displayName = "SheetTrigger";

interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  side?: "left" | "right" | "bottom";
  /** When false, renders a fixed side panel without overlay or body scroll lock. */
  modal?: boolean;
  /** When false, clicking the overlay does not close the sheet. Ignored when modal is false. */
  dismissOnOverlayClick?: boolean;
}

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ className, children, side = "right", modal = true, dismissOnOverlayClick = true, ...props }, ref) => {
    const { isOpen, setIsOpen } = React.useContext(SheetContext);
    const [portalContainer, setPortalContainer] =
      React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") setIsOpen(false);
      };

      if (isOpen && modal) {
        document.addEventListener("keydown", handleEscape);
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
        if (modal) {
          document.body.style.overflow = "unset";
        }
      };
    }, [isOpen, modal, setIsOpen]);

    React.useEffect(() => {
      if (isOpen) {
        setPortalContainer(document.body);
      } else {
        setPortalContainer(null);
      }
    }, [isOpen]);

    if (!isOpen || !portalContainer) return null;

    const panelClassName = cn(
      "fixed border bg-white shadow-lg duration-200 ease-in-out",
      side === "right" && "top-0 right-0 h-full w-full max-w-sm",
      side === "left" && "top-0 left-0 h-full w-full max-w-sm",
      side === "bottom" && "bottom-0 left-0 right-0 h-1/2 max-h-[50vh]",
      modal ? "z-[101]" : "z-[102]",
      className
    );

    if (!modal) {
      const content = (
        <div ref={ref} className={panelClassName} {...props}>
          {children}
        </div>
      );
      return createPortal(content, portalContainer);
    }

    const content = (
      <div className="fixed inset-0 z-[100] flex">
        <div
          className="fixed inset-0 bg-black/50"
          onClick={() => dismissOnOverlayClick && setIsOpen(false)}
          aria-hidden
        />
        <div
          ref={ref}
          className={panelClassName}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {children}
        </div>
      </div>
    );

    return createPortal(content, portalContainer);
  }
);
SheetContent.displayName = "SheetContent";

interface SheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const SheetHeader = React.forwardRef<HTMLDivElement, SheetHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col space-y-1.5 border-b px-6 py-4 text-left",
        className
      )}
      {...props}
    />
  )
);
SheetHeader.displayName = "SheetHeader";

interface SheetTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

const SheetTitle = React.forwardRef<HTMLHeadingElement, SheetTitleProps>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className
      )}
      {...props}
    />
  )
);
SheetTitle.displayName = "SheetTitle";

interface SheetCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

const SheetClose = React.forwardRef<HTMLButtonElement, SheetCloseProps>(
  ({ className, children, ...props }, ref) => {
    const { setIsOpen } = React.useContext(SheetContext);
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:pointer-events-none",
          className
        )}
        onClick={() => setIsOpen(false)}
        {...props}
      >
        {children ?? (
          <>
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span className="sr-only">Close</span>
          </>
        )}
      </button>
    );
  }
);
SheetClose.displayName = "SheetClose";

export { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetClose };
